import { motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import {
    FaBuilding,
    FaCalendarAlt,
    FaCheck,
    FaClipboardList,
    FaDownload,
    FaExclamationTriangle,
    FaEye,
    FaInbox,
    FaSearch,
    FaTimes,
    FaUsers
} from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';

const BulkRequests = () => {
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [requestsPerPage] = useState(10);
  const { apiCall } = useAuth();

  useEffect(() => {
    fetchRequests();
  }, []);

  useEffect(() => {
    filterRequests();
  }, [requests, searchTerm, filterStatus]);

  const fetchRequests = async () => {
    try {
      setLoading(true);

      console.log('🔄 FRONTEND - Fetching bulk requests...');

      // NOTE: backend now exposes consolidated requests at /api/requests
      // Passing a large limit so we get everything for the admin table
      const response = await apiCall('GET', '/api/requests?limit=500');

      console.log('📥 FRONTEND - Fetch response:', {
        success: response.success,
        dataStructure: response.data ? Object.keys(response.data) : null,
        requestsCount: response.data?.requests?.length || 0
      });

      if (response.success) {
        // Ensure we have an array to avoid runtime errors
        const raw = Array.isArray(response.data?.requests)
          ? response.data.requests
          : [];

        console.log('🔍 FRONTEND - Processing requests:', {
          rawCount: raw.length,
          firstRequest: raw[0] ? {
            id: raw[0]._id,
            status: raw[0].status,
            requestType: raw[0].requestType,
            requestedBy: raw[0].requestedBy
          } : null
        });

        // Transform payload so that UI stays unchanged
        const processed = raw.map((req) => {
          // Derive recruiter info (fallbacks for backwards-compatibility)
          const recruiterSource = req.requestedBy || {};

          const recruiter = {
            name:
              recruiterSource.companyName?.trim() ||
              `${recruiterSource.firstName || ''} ${recruiterSource.lastName || ''}`.trim() ||
              'Recruiter',
            company:
              recruiterSource.companyName?.trim() ||
              recruiterSource.company?.name ||
              '—',
            email: recruiterSource.email || ''
          };

          return {
            // keep original props
            ...req,

            // legacy props expected by UI
            recruiter,
            requestType: (req.requestType || '')
              .toLowerCase()
              .replace(/\s+/g, '-'),
            priority: (req.priority || 'medium').toLowerCase(),
            description: req.message || req.description || '',
            candidateCount: (req.candidates || []).length
          };
        });

        console.log('✅ FRONTEND - Processed requests:', {
          processedCount: processed.length,
          statuses: processed.reduce((acc, req) => {
            acc[req.status] = (acc[req.status] || 0) + 1;
            return acc;
          }, {})
        });

        setRequests(processed);
      } else {
        console.log('❌ FRONTEND - Fetch failed, setting empty array');
        setRequests([]);
      }
    } catch (error) {
      console.error('❌ FRONTEND - Failed to fetch bulk requests:', {
        error: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      // Fallback to empty list on error to prevent undefined length issues
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  const filterRequests = () => {
    let filtered = requests;

    if (searchTerm) {
      filtered = filtered.filter(req => 
        req.recruiter?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        req.recruiter?.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        req.requestType?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterStatus !== 'all') {
      filtered = filtered.filter(req => req.status === filterStatus);
    }

    setFilteredRequests(filtered);
    setCurrentPage(1);
  };

  const handleRequestAction = async (requestId, action, notes = '') => {
    try {
      console.log('🔥 FRONTEND - Bulk request action started:', {
        requestId,
        action,
        notes,
        timestamp: new Date().toISOString()
      });

      // Backend now expects /api/requests/:id/approve with { approved, reason }
      const requestBody = {
        approved: action === 'approve',
        reason: notes
      };

      console.log('📤 FRONTEND - Sending request:', {
        method: 'PUT',
        url: `/api/requests/${requestId}/approve`,
        body: requestBody
      });

      const response = await apiCall(
        'PUT',
        `/api/requests/${requestId}/approve`,
        requestBody
      );

      console.log('📥 FRONTEND - Response received:', {
        success: response.success,
        data: response.data,
        error: response.error
      });

      if (response.success) {
        console.log('✅ FRONTEND - Action successful, refreshing requests');
        fetchRequests();
        toast.success(`Request successfully ${action === 'approve' ? 'approved' : 'rejected'}`);
        setShowModal(false);
      } else {
        console.error('❌ FRONTEND - Action failed:', response.error);
        toast.error(response.error || `Failed to ${action} request`);
      }
    } catch (error) {
      console.error(`❌ FRONTEND - Error during ${action} request:`, {
        error: error.message,
        response: error.response?.data,
        status: error.response?.status,
        requestId,
        action
      });
      toast.error(error.response?.data?.message || error.message || `Failed to ${action} request`);
    }
  };

  const openModal = (request) => {
    setSelectedRequest(request);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedRequest(null);
  };

  // Pagination
  const indexOfLastRequest = currentPage * requestsPerPage;
  const indexOfFirstRequest = indexOfLastRequest - requestsPerPage;
  const currentRequests = filteredRequests.slice(indexOfFirstRequest, indexOfLastRequest);
  const totalPages = Math.ceil(filteredRequests.length / requestsPerPage);

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'luxury-badge-warning',
      'under review': 'luxury-badge-primary',
      approved: 'luxury-badge-success',
      rejected: 'luxury-badge-danger',
      reviewing: 'luxury-badge-primary',
      expired: 'luxury-badge-danger'
    };
    return (
      <span className={badges[status] || 'luxury-badge-primary'}>
        {status?.toUpperCase()}
      </span>
    );
  };

  const getRequestTypeBadge = (type) => {
    const badges = {
      'candidate-access': 'luxury-badge-primary',
      'candidate-contact-info': 'luxury-badge-primary',
      'job-posting': 'luxury-badge-success',
      'premium-features': 'luxury-badge-warning',
      'api-access': 'luxury-badge-danger',
      'data-export': 'luxury-badge-primary'
    };
    return (
      <span className={badges[type] || 'luxury-badge-primary'}>
        {type?.replace('-', ' ').toUpperCase()}
      </span>
    );
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-danger-600';
      case 'medium': return 'text-warning-600';
      case 'critical': return 'text-danger-700';
      case 'low': return 'text-success-600';
      default: return 'text-luxury-600';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4"
      >
        <div>
          <h1 className="text-3xl font-bold text-luxury-900">Bulk Requests Management</h1>
          <p className="text-luxury-600 mt-1">Review and manage bulk requests from recruiters</p>
        </div>
        <div className="flex items-center space-x-3">
          <button className="luxury-button-secondary text-sm">
            <FaDownload className="mr-2" />
            Export
          </button>
        </div>
      </motion.div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="stats-card"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-luxury-600">Total Requests</p>
              <p className="text-2xl font-bold text-luxury-900">{requests.length}</p>
            </div>
            <div className="stats-card-icon bg-primary-500">
              <FaInbox className="text-white" />
            </div>
          </div>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="stats-card"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-luxury-600">Pending</p>
              <p className="text-2xl font-bold text-luxury-900">
                {requests.filter(r => r.status === 'pending').length}
              </p>
            </div>
            <div className="stats-card-icon bg-warning-500">
              <FaClipboardList className="text-white" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="stats-card"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-luxury-600">Approved</p>
              <p className="text-2xl font-bold text-luxury-900">
                {requests.filter(r => r.status === 'approved').length}
              </p>
            </div>
            <div className="stats-card-icon bg-success-500">
              <FaCheck className="text-white" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="stats-card"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-luxury-600">Rejected</p>
              <p className="text-2xl font-bold text-luxury-900">
                {requests.filter(r => r.status === 'rejected').length}
              </p>
            </div>
            <div className="stats-card-icon bg-danger-500">
              <FaTimes className="text-white" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="luxury-card p-6"
      >
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-luxury-400" />
              <input
                type="text"
                placeholder="Search by recruiter name, company, or request type..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="luxury-input pl-10"
              />
            </div>
          </div>
          <div className="sm:w-48">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="luxury-select"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="reviewing">Reviewing</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="expired">Expired</option>
            </select>
          </div>
        </div>
      </motion.div>

      {/* Requests Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white shadow rounded-xl overflow-hidden"
      >
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Recruiter</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Request Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Priority</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Submitted</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {currentRequests.map((request) => (
                <tr key={request._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-3">
                      <div className="h-10 w-10 bg-gradient-to-br from-warning-500 to-warning-700 rounded-full flex items-center justify-center">
                        <span className="text-white font-semibold">
                          {request.recruiter?.name?.charAt(0)?.toUpperCase() || 'R'}
                        </span>
                      </div>
                      <div>
                        <p className="font-semibold text-luxury-900">{request.recruiter?.name || 'Unknown'}</p>
                        <div className="flex items-center text-sm text-luxury-500">
                          <FaBuilding className="mr-1" />
                          {request.recruiter?.company || 'No company'}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>{getRequestTypeBadge(request.requestType)}</td>
                  <td>
                    <div className="max-w-xs">
                      <p className="text-sm text-luxury-900 font-medium">{request.title}</p>
                      <p className="text-xs text-luxury-500 truncate">{request.description}</p>
                      {request.candidateCount && (
                        <div className="flex items-center text-xs text-luxury-600 mt-1">
                          <FaUsers className="mr-1" />
                          {request.candidateCount} candidates
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className={`font-semibold ${getPriorityColor(request.priority)}`}>
                      {request.priority?.toUpperCase() || 'MEDIUM'}
                    </div>
                  </td>
                  <td>
                    <div className="flex items-center text-sm text-luxury-600">
                      <FaCalendarAlt className="mr-2" />
                      {new Date(request.createdAt).toLocaleDateString()}
                    </div>
                  </td>
                  <td>{getStatusBadge(request.status)}</td>
                  <td>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => openModal(request)}
                        className="p-2 text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors duration-200"
                        title="View Details"
                      >
                        <FaEye />
                      </button>
                      
                      {request.status === 'pending' && (
                        <>
                          <button
                            onClick={() => handleRequestAction(request._id, 'approve')}
                            className="p-2 text-success-600 hover:text-success-700 hover:bg-success-50 rounded-lg transition-colors duration-200"
                            title="Approve Request"
                          >
                            <FaCheck />
                          </button>
                          <button
                            onClick={() => handleRequestAction(request._id, 'reject')}
                            className="p-2 text-danger-600 hover:text-danger-700 hover:bg-danger-50 rounded-lg transition-colors duration-200"
                            title="Reject Request"
                          >
                            <FaTimes />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-luxury-200">
            <div className="text-sm text-luxury-600">
              Showing {indexOfFirstRequest + 1} to {Math.min(indexOfLastRequest, filteredRequests.length)} of {filteredRequests.length} requests
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 text-sm border border-luxury-300 rounded-lg hover:bg-luxury-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              {[...Array(totalPages)].map((_, i) => (
                <button
                  key={i + 1}
                  onClick={() => setCurrentPage(i + 1)}
                  className={`px-3 py-1 text-sm border rounded-lg ${
                    currentPage === i + 1
                      ? 'bg-primary-600 text-white border-primary-600'
                      : 'border-luxury-300 hover:bg-luxury-50'
                  }`}
                >
                  {i + 1}
                </button>
              ))}
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 text-sm border border-luxury-300 rounded-lg hover:bg-luxury-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </motion.div>

      {/* Request Details Modal */}
      {showModal && selectedRequest && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
          onClick={closeModal}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.2 }}
            className="bg-white w-full max-w-4xl rounded-xl shadow-xl overflow-y-auto max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-luxury-900">Bulk Request Details</h2>
                <button
                  onClick={closeModal}
                  className="text-luxury-400 hover:text-luxury-600"
                >
                  ×
                </button>
              </div>

              <div className="space-y-6">
                {/* Header Info */}
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="h-16 w-16 bg-gradient-to-br from-warning-500 to-warning-700 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold text-xl">
                        {selectedRequest.recruiter?.name?.charAt(0)?.toUpperCase() || 'R'}
                      </span>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-luxury-900">{selectedRequest.recruiter?.name}</h3>
                      <p className="text-luxury-600">{selectedRequest.recruiter?.company}</p>
                      <div className="flex items-center space-x-2 mt-2">
                        {getStatusBadge(selectedRequest.status)}
                        {getRequestTypeBadge(selectedRequest.requestType)}
                        <span className={`font-semibold ${getPriorityColor(selectedRequest.priority)}`}>
                          {selectedRequest.priority?.toUpperCase() || 'MEDIUM'} PRIORITY
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-luxury-900">{selectedRequest.title}</p>
                    <p className="text-sm text-luxury-500">
                      Submitted: {new Date(selectedRequest.createdAt).toLocaleDateString()}
                    </p>
                    {selectedRequest.deadline && (
                      <p className="text-sm text-danger-600">
                        Deadline: {new Date(selectedRequest.deadline).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </div>

                {/* Request Details */}
                <div>
                  <h4 className="font-semibold text-luxury-900 mb-3">Request Description</h4>
                  <div className="bg-luxury-50 p-4 rounded-lg">
                    <p className="text-luxury-700 whitespace-pre-wrap">{selectedRequest.description}</p>
                  </div>
                </div>

                {/* Requirements and Criteria */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-luxury-900 mb-3">Request Details</h4>
                    <div className="space-y-2 text-sm">
                      <p><span className="font-medium">Type:</span> {selectedRequest.requestType?.replace('-', ' ')}</p>
                      <p><span className="font-medium">Priority:</span> {selectedRequest.priority}</p>
                      {selectedRequest.candidateCount && (
                        <p><span className="font-medium">Candidates Requested:</span> {selectedRequest.candidateCount}</p>
                      )}
                      {selectedRequest.budget && (
                        <p><span className="font-medium">Budget:</span> ${selectedRequest.budget}</p>
                      )}
                      {selectedRequest.duration && (
                        <p><span className="font-medium">Duration:</span> {selectedRequest.duration}</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold text-luxury-900 mb-3">Recruiter Information</h4>
                    <div className="space-y-2 text-sm">
                      <p><span className="font-medium">Company:</span> {selectedRequest.recruiter?.company}</p>
                      <p><span className="font-medium">Email:</span> {selectedRequest.recruiter?.email}</p>
                      <p><span className="font-medium">Plan:</span> {selectedRequest.recruiter?.subscriptionPlan}</p>
                      <p><span className="font-medium">Previous Requests:</span> {selectedRequest.recruiter?.totalRequests || 0}</p>
                    </div>
                  </div>
                </div>

                {/* Compliance and Legal */}
                {selectedRequest.complianceInfo && (
                  <div>
                    <h4 className="font-semibold text-luxury-900 mb-3">Compliance Information</h4>
                    <div className="bg-luxury-50 p-4 rounded-lg space-y-2">
                      {selectedRequest.complianceInfo.gdprCompliant && (
                        <div className="flex items-center text-success-600">
                          <FaCheck className="mr-2" />
                          <span>GDPR Compliant</span>
                        </div>
                      )}
                      {selectedRequest.complianceInfo.dataProtection && (
                        <div className="flex items-center text-success-600">
                          <FaCheck className="mr-2" />
                          <span>Data Protection Agreement Signed</span>
                        </div>
                      )}
                      {selectedRequest.complianceInfo.contractSigned && (
                        <div className="flex items-center text-success-600">
                          <FaCheck className="mr-2" />
                          <span>Service Contract Signed</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Risk Assessment */}
                {selectedRequest.riskLevel && (
                  <div>
                    <h4 className="font-semibold text-luxury-900 mb-3">Risk Assessment</h4>
                    <div className="flex items-center space-x-4">
                      <div className={`flex items-center ${selectedRequest.riskLevel === 'high' ? 'text-danger-600' : selectedRequest.riskLevel === 'medium' ? 'text-warning-600' : 'text-success-600'}`}>
                        <FaExclamationTriangle className="mr-2" />
                        <span className="font-semibold">{selectedRequest.riskLevel?.toUpperCase()} RISK</span>
                      </div>
                      {selectedRequest.riskFactors && (
                        <div className="flex-1">
                          <p className="text-sm text-luxury-600">{selectedRequest.riskFactors.join(', ')}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Admin Notes */}
                {selectedRequest.adminNotes && (
                  <div>
                    <h4 className="font-semibold text-luxury-900 mb-3">Admin Notes</h4>
                    <div className="bg-primary-50 p-4 rounded-lg">
                      <p className="text-primary-800">{selectedRequest.adminNotes}</p>
                    </div>
                  </div>
                )}

                {/* Timeline */}
                <div>
                  <h4 className="font-semibold text-luxury-900 mb-3">Request Timeline</h4>
                  <div className="space-y-3">
                    {selectedRequest.timeline?.map((event, index) => (
                      <div key={index} className="flex items-center space-x-3 p-3 bg-luxury-50 rounded-lg">
                        <div className="h-2 w-2 bg-primary-500 rounded-full"></div>
                        <div className="flex-1">
                          <p className="font-medium text-luxury-900">{event.action}</p>
                          <p className="text-sm text-luxury-600">{new Date(event.date).toLocaleDateString()}</p>
                          {event.notes && <p className="text-sm text-luxury-500 mt-1">{event.notes}</p>}
                        </div>
                      </div>
                    )) || (
                      <p className="text-luxury-500 text-center py-4">No timeline events recorded</p>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3 pt-4 border-t border-luxury-200">
                  {selectedRequest.status === 'pending' && (
                    <>
                      <button
                        onClick={() => {
                          handleRequestAction(selectedRequest._id, 'approve');
                        }}
                        className="luxury-button-success"
                      >
                        <FaCheck className="mr-2" />
                        Approve Request
                      </button>
                      <button
                        onClick={() => {
                          handleRequestAction(selectedRequest._id, 'reject');
                        }}
                        className="luxury-button-danger"
                      >
                        <FaTimes className="mr-2" />
                        Reject Request
                      </button>
                    </>
                  )}
                  
                  <button
                    onClick={closeModal}
                    className="luxury-button-secondary"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default BulkRequests; 